import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Checkbox,
  Divider,
  ScrollArea,
  Title,
  Card,
  Flex,
} from '@mantine/core';
import { Permission, Role } from 'interfaces';
import {
  MRT_Row,
  MRT_EditActionButtons,
  MRT_TableInstance,
} from 'mantine-react-table';

interface RoleFormModalProps {
  heading: string;
  row: MRT_Row<Role>;
  table: MRT_TableInstance<Role>;
  internalEditComponents: React.ReactNode[];
  permissions: Permission[];
}

const groupPermissions = (permissions: Permission[]) => {
  const map: Record<string, Record<string, Permission>> = {};
  permissions.forEach((p) => {
    if (!map[p.resource]) map[p.resource] = {};
    map[p.resource][p.action] = p;
  });
  return map;
};

const actionOrder = ['view', 'upload', 'modify', 'assign'];
const actionLabels: Record<string, string> = {
  view: 'View',
  modify: 'Modify',
  assign: 'Assign',
  upload: 'Upload',
};

const toTitleCase = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const RoleFormModal: React.FC<RoleFormModalProps> = ({
  heading,
  row,
  table,
  internalEditComponents,
  permissions,
}) => {
  const groupedByResource = useMemo(
    () => groupPermissions(permissions),
    [permissions]
  );
  const [selectedPermissionIDs, setSelectedPermissionIDs] = useState<
    Set<string>
  >(new Set());

  // Initialize selection from existing row values if editing
  useEffect(() => {
    const preselected = (
      (row.getValue('permissions') as Permission[]) || []
    ).map((p) => p.id);
    if (preselected.length) {
      setSelectedPermissionIDs(new Set(preselected));
    }
  }, []);

  // Keep role permissions value in sync so save handler receives it
  useEffect(() => {
    const selectedPermissions = permissions.filter((p) =>
      selectedPermissionIDs.has(p.id)
    );
    row._valuesCache = {
      ...row._valuesCache,
      permissions: selectedPermissions,
    };
  }, [selectedPermissionIDs, permissions, row]);

  const handleSelect = useCallback(
    (resource: string, action: string) => {
      const actions = groupedByResource[resource] || {};
      const targetID = actions[action]?.id;

      setSelectedPermissionIDs((prev) => {
        const next = new Set(prev);
        if (targetID) {
          if (next.has(targetID)) {
            next.delete(targetID);
          } else {
            next.add(targetID);
          }
        }
        return next;
      });
    },
    [groupedByResource]
  );

  const filteredEditComponents = internalEditComponents.filter((comp: any) => {
    if (!React.isValidElement(comp)) return true;
    const key = comp.key as string | undefined;
    if (key && key.toLowerCase().includes('permissions')) return false;
    return true;
  });

  return (
    <Stack
      data-testid={`role-form-modal-${heading
        .replace(/\s+/g, '-')
        .toLowerCase()}`}
    >
      <Title order={3} data-testid="role-form-title">
        {heading}
      </Title>
      <Card shadow="md" radius="md" padding="md" withBorder>
        <Stack>
          {filteredEditComponents}
          <Divider label="Permissions" labelPosition="center" my="sm" />
          <ScrollArea h={300} offsetScrollbars>
            <Stack>
              {Object.entries(groupedByResource).map(([resource, actions]) => {
                const actionKeys = Object.keys(actions).sort((a, b) => {
                  const aIndex = actionOrder.indexOf(a);
                  const bIndex = actionOrder.indexOf(b);
                  if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                });
                return (
                  <Group
                    key={resource}
                    justify="space-between"
                    wrap="nowrap"
                    data-testid={`perm-row-${resource}`}
                  >
                    <Text fw={500}>{toTitleCase(resource)}</Text>
                    <Group gap="lg">
                      {actionKeys.map((action) => {
                        const permissionID = actions[action]?.id;
                        const label =
                          actionLabels[action] || toTitleCase(action);
                        return (
                          <Checkbox
                            key={`${resource}-${action}`}
                            size="sm"
                            label={label}
                            checked={
                              !!permissionID &&
                              selectedPermissionIDs.has(permissionID)
                            }
                            onChange={() => handleSelect(resource, action)}
                            disabled={!permissionID}
                            data-testid={`perm-${resource}-${action}`}
                          />
                        );
                      })}
                    </Group>
                  </Group>
                );
              })}
              {Object.keys(groupedByResource).length === 0 && (
                <Text c="dimmed" size="sm" data-testid="no-permissions">
                  No permissions available.
                </Text>
              )}
            </Stack>
          </ScrollArea>
          <Text size="xs" c="dimmed" mt="xs" data-testid="permissions-help">
            Select the actions to grant for each resource.
          </Text>
        </Stack>
      </Card>
      <Flex justify="end">
        <MRT_EditActionButtons row={row} table={table} variant="text" />
      </Flex>
    </Stack>
  );
};

export default RoleFormModal;
